import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import { Seo, softwareApplication } from '~/marketing/Seo';
import { Card, Button } from '~/marketing/ui';
import { routes } from '~/marketing/routes';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { AEO_DATA, SCHEDULER_LIVE, quoted } from '~/marketing/marketingData';

/* These used to be declared here. They moved to marketingData.js when the
   SoftwareApplication JSON-LD started publishing them as machine-readable
   offers — a price stated in two places is how a site advertises one number
   and a checkout charges another. */
const TIERS = AEO_DATA.plans;
/* ENGINES, SAMPLES and CADENCE describe weekly three-sample collection nothing
   drives until the scheduler exists; they render only when SCHEDULER_LIVE is true.
   Refunds are deliberately absent: the terms live on the in-app cancellation screen.

   **Those three are Class 2 and were declared here**, which made this page the
   only source of three sanctioned sentences — the same shape as the price list
   above, and for four days after their release nothing compared them to
   `copy-rules.md` in either direction. They are read from `quoted` now. The
   other three rows are page copy and stay written here. */
const INCLUDED = [
  { tag: 'AI ASSISTANTS', label: quoted.engine_coverage_row, scheduler: true },
  { tag: 'SAMPLES', label: quoted.sampling_row, scheduler: true },
  { tag: 'CADENCE', label: quoted.cadence_row, scheduler: true },
  { tag: 'EVERY ANSWER', label: 'Every collected answer counted, and the denominator shown beside the count' },
  { tag: 'GEOGRAPHY', label: 'Each business is measured in its own city: every question names it, and Google is searched from there too' },
  { tag: 'OUTPUT', label: 'Who else is named, and the sites the answers cite: where to get listed and which pages are worth writing' },
];
const usd = (n) => '$' + n.toLocaleString('en-US');
const sec = { display: 'grid', gap: 'var(--space-6)' };
const rule = { borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-4)' };
const small = { fontSize: 'var(--fs-body-sm)', color: 'var(--text-body)', margin: 0 };
const kv = { display: 'flex', justifyContent: 'space-between', gap: 12 };
const toggle = (on) => ({ height: 34, padding: '0 18px', border: 0, borderRadius: 'var(--r-full)', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', fontWeight: 'var(--fw-medium)', background: on ? 'var(--surface-raised)' : 'transparent', color: on ? 'var(--text-strong)' : 'var(--text-muted)' });

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const included = INCLUDED.filter((f) => SCHEDULER_LIVE || !f.scheduler);
  return (
    <>
      <Seo
        title="Pricing"
        description="$79, $299 or $699 a month for 1, 6 or 15 businesses on five AI assistants. 14 days free, no credit card; an annual plan is twelve months for the price of ten."
        path={routes.pricing}
        schema={[ softwareApplication ]}
      />
      <main className="sp-page" style={{ paddingBlock: 'clamp(40px,5vw,72px)', display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 'var(--space-16)' }}>
        <header style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: '64ch' }}>
          <span className="sp-label">Pricing</span>
          <h1 style={{ fontSize: 'var(--fs-display-2)', lineHeight: 1.06 }}>One number decides your price: how many businesses you track.</h1>
          <p style={{ color: 'var(--text-body)', fontSize: 'var(--fs-body-lg)', margin: 0 }}>Every plan includes every feature and measures the same way, with the same five AI assistants. The only difference is how many businesses it covers.</p>
        </header>

        <section style={sec}>
          <Card variant="inset">
            <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
              <div style={{ display: 'grid', gap: 6 }}>
                <span className="sp-label">In every plan</span>
                <h2 style={{ fontSize: 'var(--fs-h2)' }}>The whole product, on every tier.</h2>
              </div>
              <div style={{ display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'repeat(auto-fit,minmax(min(230px,100%),1fr))' }}>
                {included.map((f) => (
                  <div key={f.tag} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '9px 0', borderTop: '1px solid var(--border-subtle)' }}>
                    <span className="sp-mono" style={{ fontSize: 11, color: 'var(--teal)', flex: 'none' }}>{f.tag}</span>
                    <span style={small}>{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </section>

        <section style={sec}>
          <div style={{ ...rule, display: 'flex', justifyContent: 'space-between', gap: 'var(--space-5)', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'grid', gap: 6 }}>
              <h2 style={{ fontSize: 'var(--fs-h2)' }}>Pick your business count.</h2>
              <span className="sp-caption">A location counts the same as a client, so a three-location plumber and a three-client marketing agency buy the same plan.</span>
            </div>
            <div style={{ display: 'grid', gap: 8, justifyItems: 'end' }}>
              <div role="group" aria-label="Billing period" style={{ display: 'inline-flex', padding: 3, border: '1px solid var(--border)', borderRadius: 'var(--r-full)', background: 'var(--surface-inset)' }}>
                <button type="button" aria-pressed={!annual} onClick={() => setAnnual(false)} style={toggle(!annual)}>Monthly</button>
                <button type="button" aria-pressed={annual} onClick={() => setAnnual(true)} style={toggle(annual)}>Annual</button>
              </div>
              <span className="sp-mono" style={{ fontSize: 11, color: 'var(--teal)' }}>An annual plan is billed once a year, up front: twelve months for the price of ten when you stay the year.</span>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 'var(--space-5)', gridTemplateColumns: 'repeat(auto-fit,minmax(min(280px,100%),1fr))' }}>
            {TIERS.map((t) => (
              /* A column that fills the row, with the button and the allowance
                 pushed to the bottom, so the three Buy buttons line up whatever
                 the text above them runs to — 2026-10-28, found by the founder:
                 Agency's shorter description put its button a line higher. */
              <Card key={t.name} variant={t.focal ? 'signal' : 'default'} style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 'var(--fw-semibold)', fontSize: 20, letterSpacing: 'var(--tr-h2)' }}>{t.name}</span>
                    <span className="sp-mono" style={{ fontSize: 11, letterSpacing: '.09em', textTransform: 'uppercase', color: t.focal ? 'var(--amber)' : 'var(--text-muted)' }}>{t.tag}</span>
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span className="sp-mono" style={{ fontSize: 'var(--fs-num-lg)', lineHeight: 1, fontWeight: 'var(--fw-medium)', color: 'var(--text-strong)' }}>{usd(annual ? t.monthly * 10 : t.monthly)}</span>
                      <span className="sp-mono" style={{ fontSize: 13, color: 'var(--text-muted)' }}>{annual ? '/ year' : '/ month'}</span>
                    </div>
                    <span className="sp-mono" style={{ fontSize: 12, color: 'var(--teal)' }}>
                      {annual ? 'Billed once a year, up front · two months free when you stay the year · ' + usd(t.monthly) + ' a month if you pay monthly' : 'Or ' + usd(t.monthly * 10) + ' billed once a year: twelve months for the price of ten'}
                    </span>
                  </div>
                  <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)', display: 'grid', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                      <span className="sp-mono" style={{ fontSize: 'var(--fs-h3)', lineHeight: 1, color: 'var(--text-strong)' }}>{t.count}</span>
                      <span style={small}>{t.unit}</span>
                    </div>
                    <span style={small}>{t.who}</span>
                  </div>
                  {/* Buys this plan, at the period the toggle shows — 2026-10-27. The
                      trial is the header's "Start free", not a second button here:
                      a card sells the plan it prices. */}
                  <div style={{ marginTop: 'auto', display: 'grid', gap: 'var(--space-5)' }}>
                    <Button block variant={t.focal ? 'primary' : 'secondary'} href={`${routes.signUp}?plan=${t.key}&interval=${annual ? 'year' : 'month'}`}>{`Buy ${t.name}`}</Button>
                    <span className="sp-mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.allowance} queries a month included</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <p className="sp-caption" style={{ maxWidth: '74ch' }}>A query is one question asked of one AI assistant, once. The allowance grows with your business count and covers every question your businesses are asked each month.</p>
        </section>

        <section style={sec}>
          <div style={rule}><h2 style={{ fontSize: 'var(--fs-h2)' }}>Buying and changing a plan</h2></div>
          <div style={{ display: 'grid', gap: 'var(--space-5)', gridTemplateColumns: 'repeat(auto-fit,minmax(min(260px,100%),1fr))' }}>
            <Card><div style={{ display: 'grid', gap: 10 }}><span className="sp-label">Starting a plan</span><p style={small}>Buy any plan on this page and it starts the day you pay. On a free trial, buying a plan ends the trial, and billing starts that day.</p></div></Card>
            {/* "Above fifteen" went 2026-10-26: it said to add a second Agency
                plan, and an account holds one. A plan for more than fifteen is in
                PROJECT_STATE's Next Steps. */}
            <Card><div style={{ display: 'grid', gap: 10 }}><span className="sp-label">Changing tiers</span><p style={small}>Up or down from your Plan page. Adding a business within your plan costs nothing extra, and its first reading starts when it is set up.</p></div></Card>
          </div>
        </section>

        <section style={sec}>
          <div style={rule}><h2 style={{ fontSize: 'var(--fs-h2)' }}>Billing</h2></div>
          <div style={{ display: 'grid', gap: 'var(--space-5)', gridTemplateColumns: 'repeat(auto-fit,minmax(min(280px,100%),1fr))' }}>
            <Card><div style={{ display: 'grid', gap: 10 }}>
              <span className="sp-label">Merchant of record</span>
              <span className="sp-mono" style={{ fontSize: 'var(--fs-h4)', color: 'var(--text-strong)' }}>Stripe</span>
              <p style={small}>Stripe appears on your card statement and issues your invoices. Stripe, our billing partner, handles checkout and your payment details.</p>
            </div></Card>
            <Card><div style={{ display: 'grid', gap: 10 }}>
              <span className="sp-label">At a glance</span>
              <div style={{ display: 'grid', gap: 8 }}>
                {[['Trial', '14 days, no credit card'], ['Cancel', 'any time, from your Plan page'], ['Currency', 'USD'], ['Tax', 'added at checkout']].map(([k, v]) => (
                  <div key={k} style={kv}><span className="sp-mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{k}</span><span className="sp-mono" style={{ fontSize: 12 }}>{v}</span></div>
                ))}
              </div>
            </div></Card>
          </div>
        </section>

        <section style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-8)', display: 'flex', gap: 'var(--space-6)', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <h2 style={{ fontSize: 'var(--fs-h2)' }}>Start with the free check.</h2>
            <span className="sp-caption">One question, five AI assistants, the report emailed to you. No credit card. <Link href={routes.methodology}>How we measure</Link>.</span>
          </div>
          <Button size="lg" href={routes.grader}>Check your visibility</Button>
        </section>
      </main>
    </>
  );
}

Pricing.layout = withMarketingLayout;
